<template>
  <div class="employee-history-tab">
    <div v-if="loading" class="text-center py-4">
      <b-spinner variant="primary" small />
      <span class="ml-2 text-muted">Đang tải lịch sử...</span>
    </div>

    <template v-else-if="histories.length">
      <b-table
        :items="histories"
        :fields="fields"
        bordered
        hover
        small
        responsive
        class="mb-0"
      >
        <template #cell(createdAt)="{ value }">
          {{ formatDateTime(value) }}
        </template>

        <template #cell(action)="{ value }">
          <b-badge :variant="actionVariant(value)">{{ actionLabel(value) }}</b-badge>
        </template>

        <template #cell(updatedByName)="{ value }">
          {{ value || '—' }}
        </template>

        <template #cell(changes)="{ item }">
          <div v-if="item.action === ACTION_CREATE" class="text-muted small">
            Tạo mới nhân viên
          </div>
          <template v-else>
            <div
              v-for="change in getDiff(item)"
              :key="change.key"
              class="change-row"
            >
              <span class="change-label">{{ change.label }}:</span>
              <span class="change-before">{{ change.before }}</span>
              <i class="fas fa-arrow-right mx-1 text-muted" style="font-size:10px" />
              <span class="change-after text-primary font-weight-bold">{{ change.after }}</span>
            </div>
            <span v-if="getDiff(item).length === 0" class="text-muted small">—</span>
          </template>
        </template>
      </b-table>
    </template>

    <p v-else class="text-muted mb-0 py-3">Chưa có lịch sử.</p>
  </div>
</template>

<script>
import moment from 'moment';
import ApiService from '@/core/services/api.service';
import { convertPrice } from '@/utils/common';

const ACTION_CREATE = 3;
const ACTION_UPDATE = 1;
const ACTION_DELETE = 2;

const FIELD_LABELS = {
  fullName: 'Họ tên',
  code: 'Mã NV',
  email: 'Email',
  mobile: 'SĐT',
  idCard: 'CCCD',
  birthDay: 'Ngày sinh',
  commencementDate: 'Ngày vào làm',
  isActive: 'Trạng thái',
  jobTitleId: 'Chức vụ',
  departmentId: 'Phòng ban',
  employeeStoreId: 'Đơn vị',
  storeId: 'Cửa hàng',
  positionSalary: 'Lương vị trí',
  insurancePaidSalary: 'Lương đóng BH',
  positionAllowance: 'PC chức vụ',
  attendanceAllowance: 'PC chuyên cần',
  transportationAllowance: 'PC xe',
  dualJobAllowance: 'PC kiêm nhiệm',
  collaborateAllowance: 'PC phối hợp',
  beautyAllowance: 'PC thẩm mỹ',
  happyCustomerAllowance: 'PC KH hạnh phúc',
  personalAllowance: 'Giảm trừ cá nhân',
  selfAllowance: 'Giảm trừ bản thân',
  dependent: 'Số NTT',
  unionDues: 'Phí công đoàn',
  shuInsuranceDeduction: 'KT BH SHU',
  standardWorkingDay: 'Ngày công chuẩn',
  standardWorkingHour: 'Giờ công chuẩn',
};

const CURRENCY_FIELDS = new Set([
  'positionSalary', 'insurancePaidSalary', 'positionAllowance', 'attendanceAllowance',
  'transportationAllowance', 'dualJobAllowance', 'collaborateAllowance', 'beautyAllowance',
  'happyCustomerAllowance', 'personalAllowance', 'selfAllowance', 'unionDues',
  'shuInsuranceDeduction',
]);

const DATE_FIELDS = new Set(['birthDay', 'commencementDate']);

export default {
  name: 'EmployeeHistoryTab',
  props: {
    employeeId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      histories: [],
      meta: { jobTitles: {}, departments: {}, stores: {} },
      ACTION_CREATE,
      fields: [
        { key: 'createdAt', label: 'Thời gian', thStyle: { width: '145px' } },
        { key: 'action', label: 'Hành động', thStyle: { width: '110px' } },
        { key: 'updatedByName', label: 'Người thực hiện', thStyle: { width: '160px' } },
        { key: 'changes', label: 'Thay đổi' },
      ],
    };
  },
  mounted() {
    this.fetchHistories();
  },
  methods: {
    async fetchHistories() {
      this.loading = true;
      try {
        const { data } = await ApiService.get(`v2/employees/${this.employeeId}/histories`);
        if (data.status === 200 || data.status === 1) {
          this.histories = data.data.histories || [];
          this.meta = data.data.meta || { jobTitles: {}, departments: {}, stores: {} };
        }
      } catch {
        // silent
      } finally {
        this.loading = false;
      }
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '—';
    },
    actionLabel(action) {
      return { [ACTION_CREATE]: 'Tạo mới', [ACTION_UPDATE]: 'Cập nhật', [ACTION_DELETE]: 'Xóa' }[action] || String(action);
    },
    actionVariant(action) {
      return { [ACTION_CREATE]: 'success', [ACTION_UPDATE]: 'primary', [ACTION_DELETE]: 'danger' }[action] || 'secondary';
    },
    formatValue(key, value) {
      if (value == null || value === '') return '—';
      if (key === 'isActive') return value == 1 || value === true ? 'Hoạt động' : 'Không hoạt động';
      if (key === 'jobTitleId') return this.meta.jobTitles[value]?.name || value;
      if (key === 'departmentId') return this.meta.departments[value]?.name || value;
      if (key === 'employeeStoreId' || key === 'storeId') return this.meta.stores[value]?.name || value;
      if (DATE_FIELDS.has(key)) return moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : value;
      if (CURRENCY_FIELDS.has(key)) return `${convertPrice(value)} đ`;
      return String(value);
    },
    getDiff(item) {
      const orig = item.originalValue || {};
      const next = item.newValue || {};
      const result = [];
      for (const [key, label] of Object.entries(FIELD_LABELS)) {
        const before = orig[key];
        const after = next[key];
        if (String(before ?? '') === String(after ?? '')) continue;
        result.push({
          key,
          label,
          before: this.formatValue(key, before),
          after: this.formatValue(key, after),
        });
      }
      return result;
    },
  },
};
</script>

<style scoped>
.change-row {
  line-height: 1.8;
  font-size: 0.85rem;
}
.change-label {
  color: #6c757d;
  margin-right: 4px;
}
.change-before {
  text-decoration: line-through;
  color: #999;
}
</style>
